import {
  buildAnnotationPath,
  parseAnnotationPath,
  FILTER_VALUE_EXISTS,
  type CheckFilter,
} from '@internal/backstage-plugin-pulse-common';
import { ENTITY_FILTER_FIELDS } from '../entity-filter-fields';

export type ScopeEntry = { kind: string; types: string[] };
export type FacetOption = { value: string; label: string };

export const compareAlphabetically = (left: string, right: string): number =>
  left.localeCompare(right);

/**
 * Entity fields a filter can be scoped by, beyond kind and type. Each key is
 * the dot path the backend filter matcher resolves against the catalog entity.
 */
export const SCOPE_FACETS = [
  'spec.lifecycle',
  'spec.owner',
  'metadata.tags',
] as const;

export type ScopeFacet = (typeof SCOPE_FACETS)[number];

/** Selected values per facet, keyed by the facet's entity path. */
export type ScopeFacetValues = Partial<Record<ScopeFacet, string[]>>;

/**
 * One annotation narrowing. An empty `values` list means "the entity carries
 * this annotation", whatever its value.
 */
export type AnnotationFilter = { key: string; values: string[] };

export const EMPTY_ANNOTATION_FILTER: AnnotationFilter = {
  key: '',
  values: [],
};

/** Everything a set of filter/exclude rules narrows by. */
export type RuleScope = {
  kinds: string[];
  typeKeys: string[];
  facets: ScopeFacetValues;
  annotations: AnnotationFilter[];
};

export const EMPTY_RULE_SCOPE: RuleScope = {
  kinds: [],
  typeKeys: [],
  facets: {},
  annotations: [],
};

// Type selections are keyed by `<kind>::<type>` so a type shared across kinds
// can be picked (and scoped) per kind.
const TYPE_KEY_SEPARATOR = '::';

export const toTypeKey = (kind: string, entityType: string): string =>
  `${kind}${TYPE_KEY_SEPARATOR}${entityType}`;

const typeKeyKind = (key: string): string => key.split(TYPE_KEY_SEPARATOR)[0];

const typeKeyType = (key: string): string =>
  key.slice(key.indexOf(TYPE_KEY_SEPARATOR) + TYPE_KEY_SEPARATOR.length);

/**
 * Rule values as a list. Hand-written definitions may use a scalar wherever the
 * form writes a list, and the filter matcher accepts both.
 */
const toRuleValues = (value: unknown): string[] => {
  if (value === undefined || value === null) {
    return [];
  }
  return (Array.isArray(value) ? value : [value]).map(String);
};

/** Parses filter/exclude rules into selected kinds, type keys and facets. */
export const parseRuleScope = (rules: CheckFilter[] | undefined): RuleScope => {
  const kinds = new Set<string>();
  const typeKeys: string[] = [];
  const facets: Partial<Record<ScopeFacet, Set<string>>> = {};
  const annotations = new Map<string, Set<string>>();
  (rules ?? []).forEach(rule => {
    const ruleKinds = toRuleValues(rule.kind).map(kind => kind.toLowerCase());
    const ruleTypes = toRuleValues(rule['spec.type']);
    if (ruleKinds.length === 0) {
      ruleTypes.forEach(entityType => typeKeys.push(toTypeKey('', entityType)));
    }
    ruleKinds.forEach(kind => {
      kinds.add(kind);
      ruleTypes.forEach(entityType =>
        typeKeys.push(toTypeKey(kind, entityType)),
      );
    });
    SCOPE_FACETS.forEach(facet => {
      const values = toRuleValues(rule[facet]);
      if (!values.length) {
        return;
      }
      const bucket = (facets[facet] ??= new Set<string>());
      values.forEach(value => bucket.add(value));
    });
    Object.entries(rule).forEach(([path, values]) => {
      const key = parseAnnotationPath(path);
      if (!key) {
        return;
      }
      const bucket = annotations.get(key) ?? new Set<string>();
      annotations.set(key, bucket);
      toRuleValues(values)
        .filter(value => value !== FILTER_VALUE_EXISTS)
        .forEach(value => bucket.add(value));
    });
  });
  return {
    kinds: [...kinds],
    typeKeys,
    facets: Object.fromEntries(
      Object.entries(facets).map(([facet, values]) => [facet, [...values]]),
    ),
    annotations: [...annotations].map(([key, values]) => ({
      key,
      values: [...values],
    })),
  };
};

const normalizedRuleScopeFilters = (rule: CheckFilter): string => {
  const facets: Partial<Record<ScopeFacet, string[]>> = {};
  SCOPE_FACETS.forEach(facet => {
    const values = [...new Set(toRuleValues(rule[facet]))].sort(
      compareAlphabetically,
    );
    if (values.length) {
      facets[facet] = values;
    }
  });

  const annotations = new Map<string, Set<string>>();
  Object.entries(rule).forEach(([path, rawValues]) => {
    const key = parseAnnotationPath(path);
    if (!key) {
      return;
    }
    const values = annotations.get(key) ?? new Set<string>();
    toRuleValues(rawValues).forEach(value => values.add(value));
    annotations.set(key, values);
  });

  const normalizedAnnotations: [string, string[]][] = [...annotations].map(
    ([key, values]) => [
      key,
      values.has(FILTER_VALUE_EXISTS)
        ? [FILTER_VALUE_EXISTS]
        : [...values].sort(compareAlphabetically),
    ],
  );

  normalizedAnnotations.sort(([left], [right]) =>
    compareAlphabetically(left, right),
  );
  return JSON.stringify({ facets, annotations: normalizedAnnotations });
};

/**
 * Whether all OR rules apply the same facet and annotation constraints. The
 * form stores one shared set of these constraints, so differing per-rule
 * values cannot be safely represented by its editors.
 */
export const hasUniformScopeFilters = (
  rules: CheckFilter[] | undefined,
): boolean => {
  const [firstRule, ...remainingRules] = rules ?? [];
  if (!firstRule) {
    return true;
  }
  const firstSignature = normalizedRuleScopeFilters(firstRule);
  return remainingRules.every(
    rule => normalizedRuleScopeFilters(rule) === firstSignature,
  );
};

/** The selected types for one kind, extracted from `<kind>::<type>` keys. */
export const typesForKind = (kind: string, typeKeys: string[]): string[] =>
  typeKeys.filter(key => typeKeyKind(key) === kind).map(typeKeyType);

/** Drops empty facet selections so they never reach the stored rule. */
const usedFacets = (facets: ScopeFacetValues): ScopeFacetValues =>
  Object.fromEntries(
    Object.entries(facets).filter(([, values]) => values?.length),
  );

/**
 * Annotation selections as rule entries. Rows without a key are dropped, and a
 * key with no selected values becomes an existence check.
 */
const annotationRuleEntries = (
  annotations: AnnotationFilter[],
): Record<string, string[]> =>
  Object.fromEntries(
    annotations
      .filter(annotation => annotation.key)
      .map(annotation => [
        buildAnnotationPath(annotation.key),
        annotation.values.length ? annotation.values : [FILTER_VALUE_EXISTS],
      ]),
  );

/**
 * One filter rule per kind, narrowed by that kind's selected types. Facet and
 * annotation selections are ANDed into every rule. When no kind is selected
 * the narrowing alone forms a single rule.
 */
export const buildFilterRules = (
  kinds: string[],
  typeKeys: string[],
  facets: ScopeFacetValues = {},
  annotations: AnnotationFilter[] = [],
): CheckFilter[] => {
  const scopedFacets = {
    ...usedFacets(facets),
    ...annotationRuleEntries(annotations),
  };
  const selectedKinds = kinds.filter(Boolean);
  const allKindsTypes = typesForKind('', typeKeys);

  if (!selectedKinds.length) {
    const allKindsRule = {
      ...(allKindsTypes.length ? { 'spec.type': allKindsTypes } : {}),
      ...scopedFacets,
    };
    return Object.keys(allKindsRule).length ? [allKindsRule] : [];
  }

  const kindRules = selectedKinds.map(kind => {
    const types = typesForKind(kind, typeKeys);
    return types.length
      ? { kind: [kind], 'spec.type': types, ...scopedFacets }
      : { kind: [kind], ...scopedFacets };
  });
  return allKindsTypes.length
    ? [{ 'spec.type': allKindsTypes, ...scopedFacets }, ...kindRules]
    : kindRules;
};

/**
 * Type options for the given kinds (or all kinds when none is selected), keyed
 * per kind so a type shared across kinds can be picked separately.
 */
export const typeOptionsFor = (
  kinds: string[],
  typesByKind: Record<string, string[]>,
): FacetOption[] => {
  if (!kinds.length) {
    return [...new Set(Object.values(typesByKind).flat())]
      .map(entityType => ({
        value: toTypeKey('', entityType),
        label: entityType,
      }))
      .sort((left, right) => compareAlphabetically(left.label, right.label));
  }

  return kinds
    .flatMap(kind =>
      (typesByKind[kind] ?? []).map(entityType => ({
        value: toTypeKey(kind, entityType),
        label: `${kind}: ${entityType}`,
      })),
    )
    .sort((left, right) => compareAlphabetically(left.label, right.label));
};

/** Groups the selected type keys under each selected kind for display. */
export const buildScope = (
  kinds: string[],
  typeKeys: string[],
): ScopeEntry[] => [
  ...(typesForKind('', typeKeys).length
    ? [{ kind: 'all kinds', types: typesForKind('', typeKeys) }]
    : []),
  ...kinds.map(kind => ({ kind, types: typesForKind(kind, typeKeys) })),
];

/** Drops selected type keys whose kind is no longer selected. */
export const pruneTypes = (kinds: string[], typeKeys: string[]): string[] =>
  typeKeys.filter(key =>
    kinds.length ? kinds.includes(typeKeyKind(key)) : typeKeyKind(key) === '',
  );

/** Human-readable labels for the scope facets, used by form fields and preview. */
export const SCOPE_FACET_LABELS: Record<ScopeFacet, string> = {
  'spec.lifecycle': ENTITY_FILTER_FIELDS.find(
    field => field.value === 'spec.lifecycle',
  )!.label,
  'spec.owner': 'Entity owner',
  'metadata.tags': 'Tag',
};

/** Shown when an annotation row narrows by presence rather than by value. */
export const ANY_ANNOTATION_VALUE_LABEL = 'any value';

/** Flattens facet and annotation selections into `Label: a, b` lines. */
export const describeFacets = (
  facets: ScopeFacetValues,
  annotations: AnnotationFilter[] = [],
): string[] => [
  ...SCOPE_FACETS.filter(facet => facets[facet]?.length).map(
    facet => `${SCOPE_FACET_LABELS[facet]}: ${facets[facet]!.join(', ')}`,
  ),
  ...annotations
    .filter(annotation => annotation.key)
    .map(
      annotation =>
        `Annotation ${annotation.key}: ${
          annotation.values.length
            ? annotation.values.join(', ')
            : ANY_ANNOTATION_VALUE_LABEL
        }`,
    ),
];
